import { useEffect, useState } from 'react'
import {
  COHORT_GOAL_FACILITATION_URL,
  GOAL_TIMELINES_URL
} from 'services/constants'
import { getRequest } from 'utils/api'
import { getGoalSteps } from 'utils/getSupportingInfo'
import FacilitationRow from './FacilitationRow'
import { Button, ButtonBase, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import InCardStack from 'components/atoms/container/InCardStack'
import moment from 'moment'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import RowStack from 'components/atoms/container/RowStack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { formatDate } from 'utils/formatDateTime'
import colors from 'styles/colors'

const getMilestoneSteps = async goalId => {
  const steps = await getGoalSteps(goalId)
  const l1Steps = steps.filter(s => s.milestoneLevel === 1)
  const milestoneSteps = l1Steps
    .map((s, i) => {
      if (l1Steps[i + 1]) {
        return {
          ...s,
          endGoalStepId: l1Steps[i + 1].id
        }
      }
      if (steps[steps.length - 1] === s) {
        return null
      }
      return {
        ...s,
        endGoalStepId: steps[steps.length - 1].id
      }
    })
    .filter(s => s !== null)
  return milestoneSteps
}

export const FacilitatedSessionsManagement = ({
  formik,
  editMode,
  cohortSessionId,
  facilitationSessions,
  setFacilitationSessions,
  preFacilitationEmails
}) => {
  const { t } = useTranslation()
  const [milestones, setMilestones] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchTimeline = async () => {
      setLoading(true)
      const milestoneSteps = await getMilestoneSteps(formik.values.path)

      const res = await getRequest(GOAL_TIMELINES_URL, {
        goalId: formik.values.path
      })
      if (res.status === 200 && milestoneSteps) {
        const timelines = res.data.data
        timelines.forEach(t => {
          const milestoneStep = milestoneSteps.find(
            s => s.endGoalStepId === t.endGoalStepId
          )
          if (milestoneStep) {
            milestoneStep.defaultTimeline = t
          }
        })
      }
      if (editMode && cohortSessionId) {
        const fRes = await getRequest(COHORT_GOAL_FACILITATION_URL, {
          cohortSessionId
        })
        let hasFacilitations = false
        if (fRes.status === 200) {
          const facData = fRes.data.data
          if (facData.length) {
            hasFacilitations = true
            facData.forEach(f => {
              const milestoneStep = milestoneSteps.find(
                s => s.endGoalStepId === f.goalTimeline?.endGoalStepId
              )
              if (milestoneStep) {
                milestoneStep.existingFacilitation = f
              }
            })
          }
        }
        setFacilitationSessions(hasFacilitations)
      }

      setMilestones(milestoneSteps)
      setLoading(false)
    }

    if (formik.values.path !== '') {
      fetchTimeline()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.path])

  useEffect(() => {
    // if facilitationSessions is false, mark all facilitations for deletion
    formik.values.facilitations.forEach(f => {
      f.deleting = !facilitationSessions
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilitationSessions])

  return (
    <>
      {facilitationSessions &&
        (formik.values.path ? (
          <>
            <Typography variant='h4'>
              {t('company.facilitationSessions')}
            </Typography>
            {loading ? (
              <CircularProgress />
            ) : (
              milestones.map((m, i) => (
                <FacilitationRow
                  key={`milestone-${i}`}
                  index={i}
                  formik={formik}
                  milestoneStep={m}
                  minDate={formik.values.startDate}
                  maxDate={formik.values.endDate}
                  preFacilitationEmails={preFacilitationEmails}
                />
              ))
            )}
          </>
        ) : (
          <Typography color='textSecondary' textAlign='center'>
            {t('company.selectPathLoadPresetPathTimelines')}
          </Typography>
        ))}
    </>
  )
}

export const FacilitatedSessionsDisplay = ({
  cohortSessionId,
  goalId,
  children
}) => {
  const { t } = useTranslation()
  const [milestones, setMilestones] = useState([])
  const [facilitationsCount, setFacilitationsCount] = useState(0)
  const [showFacilitations, setShowFacilitations] = useState(false)
  const [completedIndex, setCompletedIndex] = useState(-1)

  useEffect(() => {
    const fetchData = async () => {
      const milestoneSteps = await getMilestoneSteps(goalId)
      const fRes = await getRequest(COHORT_GOAL_FACILITATION_URL, {
        cohortSessionId
      })
      if (fRes.status === 200) {
        const facData = fRes.data.data.sort(
          (a, b) => a.facilitationDate - b.facilitationDate
        )
        if (facData.length) {
          facData.forEach((f, i) => {
            let completedI = false
            const milestoneStep = milestoneSteps.find((s, mi) => {
              if (s.endGoalStepId === f.goalTimeline?.endGoalStepId) {
                completedI = moment
                  .utc(f.facilitationDate)
                  .isBefore(moment.utc())
                  ? mi
                  : false
                return true
              }
              return false
            })
            if (milestoneStep) {
              milestoneStep.facilitation = { ...f, index: i }
              if (completedI !== false) {
                setCompletedIndex(completedI)
              }
            }
          })
          setFacilitationsCount(facData.length)
        }
      }
      setMilestones(milestoneSteps)
    }
    fetchData()
  }, [cohortSessionId, goalId])

  return (
    <>
      {children ? (
        <ButtonBase
          disabled={facilitationsCount === 0}
          onClick={() => setShowFacilitations(true)}>
          {children}
        </ButtonBase>
      ) : (
        <Button
          size='small'
          color='primary'
          variant='outlined'
          disabled={facilitationsCount === 0}
          onClick={() => setShowFacilitations(true)}>
          {t(
            facilitationsCount === 0
              ? 'company.noFacilitatedSessions'
              : 'company.facilitatedSessions'
          )}
        </Button>
      )}

      <CommonDialog
        open={showFacilitations}
        onClose={() => setShowFacilitations(false)}
        titleText={t('company.facilitatedSessions')}
        showCloseButton={true}
        bodyWidth={550}>
        <Typography variant='body1Small'>{`${milestones.length} Milestones | ${facilitationsCount} Facilitated sessions`}</Typography>
        {milestones.map((m, i) => {
          const facilitation = m.facilitation
          const completed = i <= completedIndex
          return (
            <InCardStack key={`milestone-${i}`}>
              <BoxWithBackground width='100%'>
                <RowStack justifyContent='space-between'>
                  <Typography>{`${i + 1}. ${m.name}`}</Typography>
                  {completed && <CheckCircleIcon color='success' />}
                </RowStack>
              </BoxWithBackground>
              {facilitation && (
                <BoxWithBackground
                  backgroundColor={colors.GoldenYellow50}
                  width='100%'>
                  <RowStack justifyContent='space-between'>
                    <Typography>{`-- Session ${facilitation.index + 1}`}</Typography>
                    <Typography>
                      {formatDate(facilitation.facilitationDate)}
                    </Typography>
                  </RowStack>
                </BoxWithBackground>
              )}
            </InCardStack>
          )
        })}
      </CommonDialog>
    </>
  )
}
